import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  clearTrash, getTrashCount, getTrashDetail, listTrash, purgeTrash,
  restoreTrash, restoreTrashBatch,
} from '../api/client';
import { invalidateDrawingGraph } from '../lib/graphRefresh';
import { useIsAdmin } from '../store/auth';
import type { TrashDetail, TrashEntry, TrashRestoreResult } from '../types';

/**
 * 回收站页（v0.56）。
 *
 * 需求（2026-10-16 用户原话）：
 *   「零件主数据那里的文件增减删除，在作业指导书那里能不能同步，但是有一点
 *     需要有删除备份恢复功能，如果编辑了作业指导书 删除了这些数据也没有了」
 *
 * 口径（用户三选一，全部选**更重**的那个）：
 *   · 同步范围 = 两者都要；恢复范围 = **全库回收站**（图纸 / 零件行 / BOM 关系 /
 *     文件夹四类）。
 *
 * ---------------------------------------------------------------------------
 * 界面上的三条硬约定
 * ---------------------------------------------------------------------------
 * 1. **「恢复」不是「撤销」的同义词，要说清恢复的是什么。** 因此每条都能展开
 *    看「会写回哪些表、各多少行」（`GET /api/trash/{id}`）—— 让用户点之前
 *    心里有数，而不是点完再看结果。
 * 2. **`warnings` 必须原样展示。** 后端在「对端零件已经没了 / 图纸已经归到别的
 *    零件名下」这类情况下会**跳过**并如实报数（`refs.skipped`）。只显示一句
 *    「已恢复」= 让用户以为全回来了，那比报错还危险。
 * 3. **「清空」有两个档，默认只清已恢复的。** 后端 `DELETE /api/trash` 的
 *    `only_restored` 默认 **true**（这是刻意的：这个动作最容易把还能救回来的
 *    东西一起删掉）。真·全清只有**管理员**能点，且要过二次确认。
 */

type Toast = (m: string, t?: 'info' | 'success' | 'error') => void;

/** 列表的筛选档位（`null` = 全部）。value 与后端 `entity_type` 逐字一致。 */
const TYPE_TABS: Array<{ k: string | null; label: string }> = [
  { k: null, label: '全部' },
  { k: 'drawing', label: '图纸' },
  { k: 'part', label: '零件行' },
  { k: 'bom_edge', label: 'BOM 关系' },
  { k: 'folder', label: '文件夹' },
];

/** ISO 时间 → 本地 `MM-DD HH:mm`（回收站看的是"什么时候删的"，年份多余）。 */
function fmtTime(s: string | null): string {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 恢复结果 → 一句**如实**的提示（含 warnings 与跳过的关系数）。 */
function describeRestore(r: TrashRestoreResult): string {
  const bits: string[] = [`已恢复「${r.label || r.entity_type_cn}」`];
  if (r.restored_rows) bits.push(`写回 ${r.restored_rows} 行`);
  const f = r.refs;
  if (f.folder_drawings) bits.push(`${f.folder_drawings} 张图纸回到原文件夹`);
  if (f.folder_parts) bits.push(`${f.folder_parts} 个零件回到原文件夹`);
  if (f.part_drawings) bits.push(`${f.part_drawings} 张图纸恢复归户`);
  if (f.skipped) bits.push(`${f.skipped} 项关系未恢复（见下方提示）`);
  let msg = bits.join('，');
  if (r.warnings?.length) msg += `。注意：${r.warnings.join('；')}`;
  return msg;
}

export function TrashPage({ onToast, canEdit = true }: {
  onToast: Toast;
  /**
   * v0.47/v0.56：能否**改动**（恢复 / 彻底删除 / 清空）。
   * ★ 只读账号仍然进得来、看得见列表（`GET /api/trash` 是 viewer 级）——
   *   后端写方法要 engineer。界面藏按钮只是不让人白点，真正的拦截在中间件。
   */
  canEdit?: boolean;
}) {
  const qc = useQueryClient();
  const isAdmin = useIsAdmin();
  const [type, setType] = useState<string | null>(null);
  const [withRestored, setWithRestored] = useState(false);
  /** 展开详情的那几条（id → 详情）。只按需拉，不预取全部快照。 */
  const [openIds, setOpenIds] = useState<Set<number>>(new Set());

  const listQ = useQuery({
    queryKey: ['trash', type, withRestored],
    queryFn: () => listTrash({ entityType: type ?? undefined,
                               includeRestored: withRestored }),
    refetchOnWindowFocus: false,
  });
  // 角标数（未恢复条数）—— 与当前筛选无关，单独一个轻量口。
  const countQ = useQuery({ queryKey: ['trash-count'], queryFn: () => getTrashCount(),
                            refetchOnWindowFocus: false });
  const items = useMemo(() => listQ.data?.items ?? [], [listQ.data]);

  /** 一次动作产生的多条合成一组（`batch_id` 非空且 >1 条时才值得分组）。 */
  const groups = useMemo(() => {
    const out: Array<{ batchId: string; rows: TrashEntry[] }> = [];
    const idx = new Map<string, number>();
    for (const e of items) {
      const b = e.batch_id || '';
      if (!b) { out.push({ batchId: '', rows: [e] }); continue; }
      const at = idx.get(b);
      if (at == null) { idx.set(b, out.length); out.push({ batchId: b, rows: [e] }); }
      else out[at].rows.push(e);
    }
    return out;
  }, [items]);

  /** 恢复之后要刷的：回收站自己 + **整张依赖图**（图纸↔零件↔文件夹↔SIP）。 */
  const afterChange = useCallback(() => {
    invalidateDrawingGraph(qc);
    void qc.invalidateQueries({ queryKey: ['trash'] });
    void qc.invalidateQueries({ queryKey: ['trash-count'] });
    setOpenIds(new Set());
  }, [qc]);

  const restoreMut = useMutation({
    mutationFn: (id: number) => restoreTrash(id),
    onSuccess: (r) => { onToast(describeRestore(r), r.warnings?.length ? 'info' : 'success'); afterChange(); },
    onError: (e: any) => onToast(`恢复失败：${e.message}`, 'error'),
  });

  const batchMut = useMutation({
    mutationFn: (bid: string) => restoreTrashBatch(bid),
    onSuccess: (r) => {
      const ok = r.restored.length;
      const bad = r.failed.length;
      if (!bad) {
        onToast(`整批恢复完成：${ok} 条全部回来了`, 'success');
      } else {
        // 逐条独立提交 ⇒ 如实分开报，不要用一个"成功"把失败盖掉。
        onToast(`整批恢复：成功 ${ok} 条，失败 ${bad} 条（失败的仍留在列表里可单独重试）`,
                'error');
      }
      const warns = r.restored.flatMap((x) => x.warnings ?? []);
      if (warns.length) onToast(`注意：${warns.join('；')}`, 'info');
      afterChange();
    },
    onError: (e: any) => onToast(`整批恢复失败：${e.message}`, 'error'),
  });

  const purgeMut = useMutation({
    mutationFn: (id: number) => purgeTrash(id),
    onSuccess: (r) => { onToast(`已彻底删除：${r.label || '1 条'}（不可再恢复）`, 'success'); afterChange(); },
    onError: (e: any) => onToast(`彻底删除失败：${e.message}`, 'error'),
  });

  const clearMut = useMutation({
    mutationFn: (onlyRestored: boolean) => clearTrash(onlyRestored),
    onSuccess: (r, onlyRestored) => {
      onToast(onlyRestored
        ? `已清理 ${r.purged} 条「已恢复」的历史记录`
        : `已清空 ${r.purged} 条（这些数据再也回不来了）`, 'success');
      afterChange();
    },
    onError: (e: any) => onToast(`清空失败：${e.message}`, 'error'),
  });

  const doPurge = (e: TrashEntry) => {
    if (!confirm(`彻底删除「${e.label || e.entity_type_cn}」？\n\n`
                 + `这条快照会被丢掉，**以后再也恢复不了**。\n`
                 + `（如果只是想让它别再占着列表，什么也不用做 —— 已恢复的条目可以清理）`)) return;
    purgeMut.mutate(e.id);
  };

  const doClearRestored = () => {
    if (!confirm('清理「已恢复」的历史记录？\n\n'
                 + '只删掉那些**已经还回去**、只剩审计价值的记录，\n'
                 + '列表里还能恢复的条目一条都不会动。')) return;
    clearMut.mutate(true);
  };

  const doClearAll = () => {
    const n = countQ.data ?? 0;
    if (!confirm(`⚠️ 清空回收站里全部 ${n} 条？\n\n`
                 + '这将删掉**所有还能恢复的快照**（图纸及其特征、零件行、\n'
                 + 'BOM 关系、文件夹归属），删完以后**它们再也回不来**。\n\n'
                 + '确定要继续吗？')) return;
    if (!confirm(`最后再确认一次：真的要把 ${n} 条快照全部丢掉？`)) return;
    clearMut.mutate(false);
  };

  const busy = restoreMut.isPending || batchMut.isPending || purgeMut.isPending
    || clearMut.isPending;

  const toggleDetail = (id: number) => setOpenIds((p) => {
    const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  return (
    <div className="trash-page">
      <div className="trash-head">
        <div>
          <h2 className="trash-title">
            回收站
            {!!countQ.data && <em className="trash-badge-n">{countQ.data}</em>}
          </h2>
          <div className="trash-sub">
            删除的图纸 / 零件行 / BOM 关系 / 文件夹都在这里留了一份快照 ——
            <b>包括它们的特征、SIP 草稿、文件夹归属</b>。恢复时会尽量写回原来的位置。
          </div>
        </div>
        <div className="trash-acts">
          <button className="btn sm" disabled={busy}
                  onClick={() => { listQ.refetch(); countQ.refetch(); }}>刷新</button>
          {canEdit && (
            <button className="btn sm" disabled={busy} onClick={doClearRestored}
                    title="只清理「已经恢复过」的历史记录，不动还能恢复的条目">清理已恢复记录</button>
          )}
          {canEdit && isAdmin && (
            <button className="btn sm danger" disabled={busy} onClick={doClearAll}
                    title="仅管理员：把全部快照丢掉，之后再也恢复不了">
              清空全部（不可恢复）
            </button>
          )}
        </div>
      </div>

      <div className="trash-filters">
        {TYPE_TABS.map((t) => (
          <button key={t.label}
                  className={`trash-tab${type === t.k ? ' on' : ''}`}
                  onClick={() => setType(t.k)}>{t.label}</button>
        ))}
        <span className="trash-flex" />
        <label className="trash-check" title="把已经恢复过的条目也列出来（只剩审计价值）">
          <input type="checkbox" checked={withRestored}
                 onChange={(e) => setWithRestored(e.target.checked)} />
          显示已恢复的
        </label>
      </div>

      {listQ.isLoading && <div className="trash-state">正在读回收站…</div>}
      {listQ.isError && (
        <div className="trash-state error">
          读不到回收站：{(listQ.error as any)?.message}
        </div>
      )}

      {listQ.data && items.length === 0 && (
        <div className="trash-empty">
          <div className="trash-empty-icon">🗑</div>
          <div className="trash-empty-title">
            {withRestored ? '没有任何记录' : '回收站是空的'}
          </div>
          <div className="trash-empty-desc">
            {withRestored
              ? '这个筛选下还没有条目。'
              : '删除图纸 / 零件 / BOM 关系 / 文件夹之后，都会先落到这里，可以再恢复。'}
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div className="trash-list">
          {groups.map((g) => {
            const batched = g.rows.length > 1 && !!g.batchId;
            return (
              <div className="trash-group" key={g.batchId || `s${g.rows[0].id}`}>
                {batched && (
                  <div className="trash-grouphead">
                    <span className="trash-groupnote">
                      同一次操作删除了 {g.rows.length} 条
                    </span>
                    {canEdit && (
                      <button className="btn sm primary" disabled={busy}
                              title="按原样把这批一起恢复（逐条独立执行，某条失败不影响其余）"
                              onClick={() => {
                                if (!confirm(`整批恢复这 ${g.rows.length} 条？`)) return;
                                batchMut.mutate(g.batchId);
                              }}>整批恢复（{g.rows.length}）</button>
                    )}
                  </div>
                )}
                {g.rows.map((e) => (
                  <TrashRow key={e.id} e={e} open={openIds.has(e.id)} busy={busy}
                            canEdit={canEdit}
                            onToggleDetail={() => toggleDetail(e.id)}
                            onRestore={() => restoreMut.mutate(e.id)}
                            onPurge={() => doPurge(e)} />
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ================================================================ 单条
function TrashRow({ e, open, busy, canEdit, onToggleDetail, onRestore, onPurge }: {
  e: TrashEntry;
  open: boolean;
  busy: boolean;
  canEdit: boolean;
  onToggleDetail: () => void;
  onRestore: () => void;
  onPurge: () => void;
}) {
  // ⚠️ 刻意**不按 `entity_type` 分叉渲染**：四类实体的差异全部由后端下发的
  //    `entity_type_cn` / `detail` / `tables` 表达（唯一出处）。前端再分一次叉，
  //    后端加第五类实体时这里就会静默漏掉。
  return (
    <div className={`trash-row${e.restored ? ' restored' : ''}`}>
      <div className="trash-rowmain">
        <span className={`trash-kind k-${e.entity_type}`}>{e.entity_type_cn}</span>
        <span className="trash-label" title={e.label}>{e.label || `#${e.entity_id}`}</span>
        {e.detail && <span className="trash-detail" title={e.detail}>{e.detail}</span>}
        <span className="trash-flex" />
        <span className="trash-when" title={`删除时间 ${e.deleted_at ?? ''}`}>
          {fmtTime(e.deleted_at)}
          {e.deleted_by ? ` · ${e.deleted_by}` : ''}
        </span>
        <span className="trash-acts">
          <button className="btn sm" disabled={busy} onClick={onToggleDetail}>
            {open ? '收起' : '详情'}
          </button>
          {e.restored ? (
            <span className="trash-restoredtag"
                  title={`已于 ${e.restored_at ?? ''} 由 ${e.restored_by || '—'} 恢复`}>
              已恢复 {e.restored_by ? `· ${e.restored_by}` : ''}
            </span>
          ) : canEdit && (
            <button className="btn sm primary" disabled={busy} onClick={onRestore}
                    title="把这条（连同它的特征 / 草稿 / 归属）写回去">恢复</button>
          )}
          {canEdit && (
            <button className="btn sm danger" disabled={busy} onClick={onPurge}
                    title="丢掉这份快照 —— 之后再也恢复不了">彻底删除</button>
          )}
        </span>
      </div>
      {open && <TrashDetailPanel id={e.id} />}
    </div>
  );
}

/** 展开的详情：**恢复后会写回哪些表、各多少行**（点之前先给个交代）。 */
function TrashDetailPanel({ id }: { id: number }) {
  const q = useQuery({
    queryKey: ['trash-detail', id],
    queryFn: () => getTrashDetail(id),
    refetchOnWindowFocus: false,
  });
  if (q.isLoading) return <div className="trash-detailbox">读取快照清单…</div>;
  if (q.isError) {
    return <div className="trash-detailbox error">
      读不到详情：{(q.error as any)?.message}
    </div>;
  }
  const d = q.data as TrashDetail;
  const refs = d.refs ?? {};
  return (
    <div className="trash-detailbox">
      <div className="trash-dtl-title">恢复后会写回这些表：</div>
      <div className="trash-tables">
        {(d.tables ?? []).map((t) => (
          <span className="trash-table" key={t.table}>
            <code>{t.table}</code>
            <em>{t.rows} 行</em>
          </span>
        ))}
        {(d.tables ?? []).length === 0 && <span className="trash-hint">（没有表数据）</span>}
      </div>
      {/* 关系类信息（v0.56 的快照分「实物」与「关系」两种 —— 图纸/零件是实物，
          BOM 边与文件夹归属是关系）。有就列，没有就不占位。 */}
      {Object.keys(refs).length > 0 && (
        <>
          <div className="trash-dtl-title">还会恢复这些关系：</div>
          <div className="trash-tables">
            {Object.entries(refs).map(([k, v]) => (
              <span className="trash-table" key={k}>
                <code>{k}</code>
                <em>{Array.isArray(v) ? `${(v as any[]).length} 项` : String(v)}</em>
              </span>
            ))}
          </div>
        </>
      )}
      <div className="trash-hint">
        删除时的主键 #{d.entity_id} —— 如果这个号已经被新数据用掉了，
        恢复时会自动分配一个新号（原来指向它的关系会跟着改过来）。
      </div>
    </div>
  );
}
